import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Button, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, Grid, IconButton, Chip, Switch,
  FormControlLabel, CircularProgress
} from '@mui/material';
import { Add, Edit, Delete, Refresh, Percent } from '@mui/icons-material';
import api from '../services/api';
import { toast } from 'react-hot-toast';

const GSTManagement = () => {
  const [gstRates, setGstRates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingGST, setEditingGST] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    rate: '',
    cgst_rate: '',
    sgst_rate: '',
    igst_rate: '0',
    is_active: true
  });

  useEffect(() => {
    fetchGSTRates();
  }, []);

  const fetchGSTRates = async () => {
    setLoading(true);
    try {
      const response = await api.get('/core/gst-rates/');
      let data = [];
      if (Array.isArray(response.data)) {
        data = response.data;
      } else if (response.data?.results) {
        data = response.data.results;
      }
      setGstRates(data);
    } catch (error) {
      toast.error('Failed to fetch GST rates');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (gst = null) => {
    if (gst) {
      setEditingGST(gst);
      setFormData({
        name: gst.name || '',
        rate: gst.rate || '',
        cgst_rate: gst.cgst_rate || '',
        sgst_rate: gst.sgst_rate || '',
        igst_rate: gst.igst_rate || '0',
        is_active: gst.is_active !== undefined ? gst.is_active : true
      });
    } else {
      setEditingGST(null);
      setFormData({
        name: '',
        rate: '',
        cgst_rate: '',
        sgst_rate: '',
        igst_rate: '0',
        is_active: true
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingGST(null);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    // Auto-calculate CGST and SGST when rate changes
    if (name === 'rate') {
      const rate = parseFloat(value) || 0;
      setFormData({
        ...formData,
        rate: value,
        cgst_rate: (rate / 2).toFixed(2),
        sgst_rate: (rate / 2).toFixed(2)
      });
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  const handleSwitchChange = (e) => {
    setFormData({ ...formData, is_active: e.target.checked });
  };

  const handleSubmit = async () => {
    try {
      if (!formData.name || !formData.rate) {
        toast.error('Name and Rate are required');
        return;
      }

      const submitData = {
        ...formData,
        rate: parseFloat(formData.rate) || 0,
        cgst_rate: parseFloat(formData.cgst_rate) || 0,
        sgst_rate: parseFloat(formData.sgst_rate) || 0,
        igst_rate: parseFloat(formData.igst_rate) || 0
      };

      if (editingGST) {
        await api.put(`/core/gst-rates/${editingGST.id}/`, submitData);
        toast.success('GST rate updated successfully');
      } else {
        await api.post('/core/gst-rates/', submitData);
        toast.success('GST rate created successfully');
      }
      handleCloseDialog();
      fetchGSTRates();
    } catch (error) {
      console.error('Error saving GST rate:', error);
      toast.error('Failed to save GST rate');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this GST rate?')) {
      try {
        await api.delete(`/core/gst-rates/${id}/`);
        toast.success('GST rate deleted successfully');
        fetchGSTRates();
      } catch (error) {
        toast.error('Failed to delete GST rate');
      }
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">
          <Percent sx={{ mr: 1, verticalAlign: 'middle' }} />
          GST Management
        </Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => handleOpenDialog()}
        >
          Add GST Rate
        </Button>
      </Box>

      <Paper sx={{ p: 2 }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell align="center">Rate (%)</TableCell>
                <TableCell align="center">CGST (%)</TableCell>
                <TableCell align="center">SGST (%)</TableCell>
                <TableCell align="center">IGST (%)</TableCell>
                <TableCell align="center">Status</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {gstRates.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    <Typography color="textSecondary" sx={{ py: 3 }}>
                      No GST rates found. Click "Add GST Rate" to create one.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                gstRates.map((gst) => (
                  <TableRow key={gst.id}>
                    <TableCell>
                      <Typography fontWeight="bold">{gst.name}</Typography>
                    </TableCell>
                    <TableCell align="center">{gst.rate}%</TableCell>
                    <TableCell align="center">{gst.cgst_rate}%</TableCell>
                    <TableCell align="center">{gst.sgst_rate}%</TableCell>
                    <TableCell align="center">{gst.igst_rate || 0}%</TableCell>
                    <TableCell align="center">
                      <Chip 
                        label={gst.is_active ? 'Active' : 'Inactive'}
                        color={gst.is_active ? 'success' : 'error'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="right">
                      <IconButton size="small" onClick={() => handleOpenDialog(gst)}>
                        <Edit />
                      </IconButton>
                      <IconButton size="small" color="error" onClick={() => handleDelete(gst.id)}>
                        <Delete />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* GST Rate Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingGST ? 'Edit GST Rate' : 'Add GST Rate'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Name"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                required
                helperText="e.g., GST 5%, GST 12%, GST 18%"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Total Rate (%)"
                name="rate"
                type="number"
                value={formData.rate}
                onChange={handleInputChange}
                required
                helperText="Total GST percentage (CGST + SGST)"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="CGST Rate (%)"
                name="cgst_rate"
                type="number"
                value={formData.cgst_rate}
                onChange={handleInputChange}
                helperText="Auto-calculated from total rate"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="SGST Rate (%)"
                name="sgst_rate"
                type="number"
                value={formData.sgst_rate}
                onChange={handleInputChange}
                helperText="Auto-calculated from total rate"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="IGST Rate (%)"
                name="igst_rate"
                type="number"
                value={formData.igst_rate}
                onChange={handleInputChange}
                helperText="For inter-state transactions"
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.is_active}
                    onChange={handleSwitchChange}
                    name="is_active"
                    color="primary"
                  />
                }
                label="Active"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained">
            {editingGST ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default GSTManagement;